import React from 'react';

/**
 * Inline `N / max` character counter rendered next to a field label.
 *
 * The Wawi backend exposes per-column character limits live from
 * `INFORMATION_SCHEMA.COLUMNS`. A `null` limit means the underlying column is
 * `NVARCHAR(MAX)` (no column-level cap) or the field is not a text input -
 * in both cases the spec says: render nothing, no validation, no `maxLength`
 * attribute on the input. Encoding that "render or not" decision in this
 * component keeps every call site free of conditional logic.
 *
 * Color states:
 *   - default (gray)  - usage below 90 % of limit
 *   - warn (orange)   - usage in [90 %, 100 %)
 *   - over (red)      - usage at or above limit (only reachable when the
 *                       value comes from the API, e.g. AI generation /
 *                       translation; manual typing is capped by the
 *                       `maxLength` attribute the caller sets on the input).
 */
type Props = {
  value: string | null | undefined;
  maxLength: number | null | undefined;
  className?: string;
};

const FieldLengthCounter: React.FC<Props> = ({ value, maxLength, className }) => {
  if (maxLength == null) {
    return null;
  }
  const length = (value ?? '').length;
  const ratio = maxLength > 0 ? length / maxLength : 0;
  let stateClass = '';
  if (length >= maxLength) {
    stateClass = ' content-master-length-counter--over';
  } else if (ratio >= 0.9) {
    stateClass = ' content-master-length-counter--warn';
  }
  return (
    <span className={`content-master-length-counter${stateClass}${className ? ` ${className}` : ''}`}>
      {length} / {maxLength}
    </span>
  );
};

export default FieldLengthCounter;
